// src/components/views/ContainerManagementView.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  PlayArrow as RunIcon,
  Science as TestIcon,
  Folder as FolderIcon,
  Api as ApiIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api';
import SwaggerImportModal from '../modals/SwaggerImportModal';

interface Container {
  id: string;
  name: string;
  display_name: string;
  type: string;
  type_label: string;
  icon: string;
  description: string;
  stats: {
    total_apis: number;
    total_endpoints: number;
    total_test_scenarios: number;
  };
  created_at: string;
  updated_at: string;
}

interface UILabels {
  container_singular: string;
  container_plural: string;
  container_icon: string;
  create_button: string;
  list_title: string;
  select_placeholder: string;
  empty_state: string;
  delete_confirm: string;
}

export const ContainerManagementView: React.FC = () => {
  const [containers, setContainers] = useState<Container[]>([]);
  const [uiLabels, setUILabels] = useState<UILabels | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [swaggerImportOpen, setSwaggerImportOpen] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);

  // Create form
  const [newContainerName, setNewContainerName] = useState('');
  const [newContainerDescription, setNewContainerDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadContainers();
  }, []);

  const loadContainers = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/document_intelligence/containers/list`);
      const data = await response.json();

      if (response.ok) {
        setContainers(data.containers || []);
        setUILabels(data.ui_labels);
      } else {
        throw new Error(data.detail || 'Failed to load containers');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContainer = async () => {
    if (!newContainerName.trim()) {
      setError('Container name is required');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/document_intelligence/containers/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newContainerName,
          description: newContainerDescription
        })
      });

      const data = await response.json();

      if (response.ok) {
        setCreateDialogOpen(false);
        setNewContainerName('');
        setNewContainerDescription('');
        loadContainers();
      } else {
        throw new Error(data.detail || 'Failed to create container');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleImportSwagger = (container: Container) => {
    setSelectedContainer(container);
    setSwaggerImportOpen(true);
  };

  const handleImportComplete = () => {
    setSwaggerImportOpen(false);
    loadContainers(); // Refresh to get updated stats
  };

  const renderEmptyState = () => (
    <Paper
      sx={{
        p: 6,
        textAlign: 'center',
        backgroundColor: 'background.default'
      }}
    >
      <Box sx={{ fontSize: 64, mb: 2 }}>
        {uiLabels?.container_icon || '📁'}
      </Box>
      <Typography variant="h6" gutterBottom>
        {uiLabels?.empty_state || 'No containers yet'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create your first {uiLabels?.container_singular.toLowerCase()} to organize your APIs
      </Typography>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setCreateDialogOpen(true)}
        size="large"
      >
        {uiLabels?.create_button || 'Create Container'}
      </Button>
    </Paper>
  );

  const renderContainerCard = (container: Container) => (
    <Grid item xs={12} sm={6} md={4} key={container.id}>
      <Card
        elevation={2}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          }
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ fontSize: 40, mr: 2 }}>
              {container.icon}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                {container.display_name}
              </Typography>
              <Chip
                label={container.type_label}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </Box>

          {container.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {container.description}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {container.stats.total_apis}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  APIs
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {container.stats.total_endpoints}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Endpoints
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {container.stats.total_test_scenarios}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tests
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Box>
            <Tooltip title="Import Swagger">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleImportSwagger(container)}
              >
                <UploadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="View APIs">
              <IconButton
                size="small"
                color="primary"
                onClick={() => {/* Navigate to APIs view */}}
              >
                <ApiIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Generate Tests">
              <IconButton
                size="small"
                color="primary"
                onClick={() => {/* Navigate to test generation */}}
              >
                <TestIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip title="Settings">
              <IconButton size="small">
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardActions>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {uiLabels?.list_title || 'My Containers'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your {uiLabels?.container_plural.toLowerCase() || 'containers'} and APIs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadContainers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {uiLabels?.create_button || 'Create'}
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Container Grid */}
      {containers.length === 0 ? (
        renderEmptyState()
      ) : (
        <Grid container spacing={3}>
          {containers.map(renderContainerCard)}
        </Grid>
      )}

      {/* Create Container Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {uiLabels?.create_button || 'Create Container'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={`${uiLabels?.container_singular || 'Container'} Name`}
            fullWidth
            value={newContainerName}
            onChange={(e) => setNewContainerName(e.target.value)}
            placeholder="e.g., CRM Application, Finance Module"
            helperText="Enter a descriptive name for your container"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            multiline
            rows={3}
            value={newContainerDescription}
            onChange={(e) => setNewContainerDescription(e.target.value)}
            placeholder="Describe what this container is for..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateContainer}
            variant="contained"
            disabled={creating || !newContainerName.trim()}
            startIcon={creating ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Swagger Import Modal */}
      {selectedContainer && (
        <SwaggerImportModal
          open={swaggerImportOpen}
          onClose={() => setSwaggerImportOpen(false)}
          containerId={selectedContainer.id}
          containerName={selectedContainer.display_name}
          onImportComplete={handleImportComplete}
        />
      )}
    </Box>
  );
};

export default ContainerManagementView;